import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { Auth } from 'aws-amplify'
import { API } from 'src/utils/interceptor'
import Sentry from 'src/utils/sentry'

const supplierPortalOrdersRequest = createAsyncThunk(
  'adminOrders/fetchSupplierPortalOrders',
  async ({ from, to, page, limit, search }) => {
    try {
      const query = new URLSearchParams({
        start: from,
        end: to,
        page,
        limit,
        search,
      })
      return await API.get('api2', `suppliers/all/orders?${query}`)
    } catch (error) {
      Sentry.captureException(error)
      throw error
    }
  },
)

const adminOrderSlice = createSlice({
  name: 'adminOrders',
  initialState: {
    adminOrders: [],
    isLoading: false,
    pagination: { total: 0, page: 1, limit: 50 },
    supplierOrdersRequestId: null,
    hasIntegratedSuppliers: false,
  },
  reducers: {
    setAdminOrders: (state, action) => {
      state.supplierOrdersRequestId = null
      state.hasIntegratedSuppliers = Boolean(
        action.payload.hasIntegratedSuppliers,
      )
      state.adminOrders = action.payload.orders || action.payload
      if (action.payload.pagination)
        state.pagination = action.payload.pagination
    },
    setIsLoading: (state, action) => {
      state.isLoading = action.payload
    },
  },
  extraReducers: builder => {
    builder
      .addCase(supplierPortalOrdersRequest.pending, (state, action) => {
        state.supplierOrdersRequestId = action.meta.requestId
        state.isLoading = true
      })
      .addCase(supplierPortalOrdersRequest.fulfilled, (state, action) => {
        if (state.supplierOrdersRequestId !== action.meta.requestId) return
        state.adminOrders = action.payload.orders
        state.pagination = action.payload.pagination
        state.hasIntegratedSuppliers = Boolean(
          action.payload.hasIntegratedSuppliers,
        )
        state.supplierOrdersRequestId = null
        state.isLoading = false
      })
      .addCase(supplierPortalOrdersRequest.rejected, (state, action) => {
        if (state.supplierOrdersRequestId !== action.meta.requestId) return
        state.adminOrders = []
        state.hasIntegratedSuppliers = false
        state.pagination = { total: 0, page: 1, limit: action.meta.arg.limit }
        state.supplierOrdersRequestId = null
        state.isLoading = false
      })
  },
})

export const { setAdminOrders, setIsLoading } = adminOrderSlice.actions

export const fetchAdminOrders =
  (FROM_DATE, TO_DATE, page = 1, limit = 50) =>
  async dispatch => {
    try {
      dispatch(setIsLoading(true))
      const session = await Auth.currentAuthenticatedUser()
      const url = `adminOrders?start=${FROM_DATE}&end=${TO_DATE}&username=${session.attributes.sub}&page=${page}&limit=${limit}`
      const data = await API.get('api', url)
      dispatch(setAdminOrders(data))
      dispatch(setIsLoading(false))
    } catch (e) {
      console.error(e)
      dispatch(setIsLoading(false))
      Sentry.captureException(e)
      return false
    }
  }

export const fetchSupplierPortalOrders = (
  from,
  to,
  page = 1,
  limit = 50,
  search = '',
) => supplierPortalOrdersRequest({ from, to, page, limit, search })

export const getListOrders = supplierId => async dispatch => {
  try {
    dispatch(setIsLoading(true))
    const data = await API.get('api2', `suppliers/${supplierId}/orders`)
    dispatch(setAdminOrders(data.orders))
    dispatch(setIsLoading(false))
  } catch (e) {
    console.error(e)
    dispatch(setIsLoading(false))
    Sentry.captureException(e)
    return false
  }
}

export const searchOrders = (supplierId, title) => async dispatch => {
  try {
    dispatch(setIsLoading(true))
    const data = await API.get(
      'api2',
      `suppliers/${supplierId}/orders?title=${title}`,
    )
    dispatch(setAdminOrders(data.orders))
    dispatch(setIsLoading(false))
  } catch (e) {
    console.error(e)
    dispatch(setIsLoading(false))
    Sentry.captureException(e)
    return false
  }
}

export const searchOrdersByPeriod =
  (supplierId, fromDate, toDate) => async dispatch => {
    try {
      dispatch(setIsLoading(true))
      const data = await API.get(
        'api2',
        `suppliers/${supplierId}/orders?toDate=${toDate}&fromDate=${fromDate}`,
      )
      dispatch(setAdminOrders(data.orders))
      dispatch(setIsLoading(false))
    } catch (e) {
      console.error(e)
      dispatch(setIsLoading(false))
      Sentry.captureException(e)
      return false
    }
  }

export default adminOrderSlice
